// CR485: Admin Snippet Artifacts Viewer — per-batch snippet diagnostics
// CR518: Snippet preview drawer with bbox overlay + deep link support
// CR544: Attempt selector for Diamond Mode — show outputs per attempt
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Switch } from "@/components/ui/switch";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ImageIcon, ArrowLeft, Eye, ArrowDown, ArrowUp, CheckCircle, XCircle, AlertCircle, FileImage, HelpCircle, RefreshCw, Copy, Gem, Square, Wrench, ClipboardCopy
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SnippetPreviewDrawer } from "@/components/admin/snippets/SnippetPreviewDrawer";
import { safeBboxDelta, classifyDelta, type BboxDelta, type DeltaClass } from "@/lib/snippets/bboxDelta";

// CR544: Attempt metadata from edge function
interface AttemptInfo {
  id: string;
  attempt_no: number;
  status: string;
  created_at: string;
  finished_at: string | null;
  records_out: number | null;
  artifacts_attempted: number | null;
  // CR544A: diagnostics fields for Diamond Mode display
  effective_run_policy?: string | null;
  effective_recompute_policy?: string | null;
  fn_version?: string | null;
  skipped_reason?: string | null;
  // CR546: Write scope diagnostics
  artifact_write_scope?: string | null;
  artifact_step_attempt_id_used?: string | null;
  diamond_guard_active?: boolean | null;
}

interface SnippetRow {
  staging_item_id: string | null;
  deal_id: string | null;
  page_number: number | null;
  snippet_asset_path: string | null;
  page_image_path: string | null;
  bbox_norm: unknown;
  snippet_status: string | null;
  bbox_area_ratio: number | null;
  bbox_quality_hint: string | null;
  bbox_reject_reason: string | null;
  bbox_confidence: number | null;
  render_source: string | null;
  s5: unknown;
  // CR486 scoring fields
  bbox_score: number | null;
  bbox_score_components: {
    key_phrase_hits: number;
    area_penalty: number;
    overlap_penalty: number;
  } | null;
  bbox_locator_pass: string | null;
  locator_reason: string | null;
  // CR514: source page fields
  source_page_path: string | null;
  source_page_bucket: string | null;
  // CR518: Pass A/B diagnostics
  pass_a_bbox_norm?: unknown;
  pass_b?: unknown;
  cropper_used?: boolean;
  final_bbox_source?: string | null;
  // CR534: Effective thresholds
  min_cropper_confidence_used?: number | null;
  min_locator_confidence_used?: number | null;
  // CR544 Phase 2: Copy-forward metadata
  copied_forward?: boolean;
  copied_from_attempt_id?: string | null;
  copy_reason?: string | null;
  // CR544 Phase 3: Attempt tracking
  step_attempt_id?: string | null;
  // CR520: Evidence scoring
  evidence_score?: number | null;
  evidence_hits?: { brand?: number; value?: number; mechanic?: number; total?: number } | null;
  // CR575: Locator proposed + anchor gate
  locator_proposed_bbox_norm?: unknown;
  locator_proposed_area_ratio?: number | null;
  locator_proposed_confidence?: number | null;
  locator_proposed_quality_hint?: string | null;
  anchor_gate_result?: string | null;
  anchor_gate_reject_reason?: string | null;
  anchor_gate_must_hit_count?: number | null;
  anchor_gate_should_hit_count?: number | null;
  anchor_gate_must_hit_required?: number | null;
  anchor_gate_anchor_hits?: string[] | null;
  // CR580: Per-box OCR diagnostics
  box_ocr_engine?: string | null;
  box_ocr_status?: string | null;
  anchor_gate_rule_version?: string | null;
  // CR581: OCR sanity diagnostics
  ocr_sanity_status?: string | null;
  // CR582: Page OCR source diagnostics
  ocr_page_source?: string | null;
}

function isPageFallbackRow(s: SnippetRow): boolean {
  const status = (s.snippet_status ?? "").toLowerCase();
  return status === "page_fallback" || (!!s.snippet_asset_path && !!s.page_image_path && s.snippet_asset_path === s.page_image_path);
}

function hasTrueCroppedSnippet(s: SnippetRow): boolean {
  return !!s.snippet_asset_path && !isPageFallbackRow(s);
}

// CR578/CR580: Derived locator status taxonomy
type LocatorDebugStatus = 'ok' | 'ok_soft' | 'ok_soft_fallback' | 'truth_override' | 'gate_failed' | 'ocr_mismatch' | 'no_match_on_page' | 'last_resort' | 'cropper_only' | 'full_page_ok' | 'unknown';

function deriveLocatorStatus(s: SnippetRow): LocatorDebugStatus {
  // CR585: Truth override takes priority
  if ((s.snippet_status ?? '').toLowerCase() === 'locator_truth_override' || s.final_bbox_source === 'truth_override') return 'truth_override';
  if (s.anchor_gate_result === 'ocr_mismatch') return 'ocr_mismatch';
  if (s.anchor_gate_result === 'passed_soft_fallback') return 'ok_soft_fallback';
  if (s.anchor_gate_result === 'passed_soft') return 'ok_soft';
  if (s.anchor_gate_result === 'rejected') return 'gate_failed';
  if ((s.snippet_status ?? '').toLowerCase() === 'located_full_page_ok') return 'full_page_ok';
  if (
    ((s.snippet_status ?? '').toLowerCase() === 'not_localizable' ||
     (s.snippet_status ?? '').toLowerCase() === 'locator_no_match_on_page') &&
    !s.locator_proposed_bbox_norm
  ) return 'no_match_on_page';
  if (s.final_bbox_source === 'last_resort') return 'last_resort';
  if (s.final_bbox_source === 'locator_soft') return 'ok_soft_fallback';
  if (s.final_bbox_source === 'cropper') return 'cropper_only';
  if (s.final_bbox_source === 'locator') return 'ok';
  const st = (s.snippet_status ?? '').toLowerCase();
  if (st === 'located' || st === 'cropped' || st === 'locator_ok' || st === 'locator_ok_anchor_soft' || st === 'locator_ok_soft_fallback') return 'ok';
  return 'unknown';
}

const LOCATOR_STATUS_CONFIG: Record<LocatorDebugStatus, { label: string; color: string }> = {
  ok:               { label: 'ok',             color: 'bg-green-600' },
  ok_soft:          { label: 'ok (soft)',      color: 'bg-teal-600' },
  ok_soft_fallback: { label: 'ok (ghost)',     color: 'bg-cyan-600' },
  truth_override:   { label: 'truth',          color: 'bg-emerald-600' },
  gate_failed:      { label: 'gate_failed',    color: 'bg-destructive' },
  ocr_mismatch:     { label: 'ocr_mismatch',   color: 'bg-amber-500' },
  no_match_on_page: { label: 'no_match',       color: 'bg-secondary' },
  last_resort:      { label: 'last_resort',    color: 'bg-amber-600' },
  cropper_only:     { label: 'cropper_only',   color: 'bg-sky-600' },
  full_page_ok:     { label: 'full_page_ok',   color: 'bg-teal-600' },
  unknown:          { label: 'unknown',         color: '' },
};

function locatorStatusBadge(status: LocatorDebugStatus, row?: SnippetRow) {
  const cfg = LOCATOR_STATUS_CONFIG[status];
  if (status === 'unknown') return <Badge variant="outline" className="text-[10px]">—</Badge>;
  // CR580: OCR mismatch — amber badge with merchant hint
  if (status === 'ocr_mismatch' && row) {
    const hint = (row as any).ocr_page_merchant_hint;
    const hintStr = hint?.ocr_source_merchant_id
      ? ` (OCR: ${String(hint.ocr_source_merchant_id).slice(0, 15)})`
      : '';
    return <Badge className={`text-[10px] text-white ${cfg.color}`}>ocr_mismatch{hintStr}</Badge>;
  }
  // CR578: Show core/support counts inline for gate_failed
  if (status === 'gate_failed' && row) {
    const coreHits = (row as any).proposal_core_hits;
    const coreTotal = (row as any).proposal_core_total;
    const supportHits = (row as any).proposal_support_hits;
    const totalHits = ((row as any).proposal_box_must_hit_count ?? 0) + ((row as any).proposal_box_should_hit_count ?? 0);
    const minTotal = (row as any).anchor_gate_threshold_min_total ?? 3;
    const countStr = coreHits != null
      ? ` (core ${coreHits}/${coreTotal ?? '?'}, total ${totalHits}/${minTotal})`
      : '';
    return <Badge className={`text-[10px] text-white ${cfg.color}`}>{cfg.label}{countStr}</Badge>;
  }
  return <Badge className={`text-[10px] text-white ${cfg.color}`}>{cfg.label}</Badge>;
}

function qualityBadge(hint: string | null) {
  if (!hint) return <Badge variant="outline" className="text-xs">Unknown</Badge>;
  switch (hint) {
    case "tight": return <Badge className="bg-green-600 text-xs">Tight</Badge>;
    case "medium": return <Badge className="bg-primary text-xs">Medium</Badge>;
    case "loose": return <Badge variant="secondary" className="text-xs">Loose</Badge>;
    case "bad": return <Badge variant="destructive" className="text-xs">Bad</Badge>;
    default: return <Badge variant="outline" className="text-xs">{hint}</Badge>;
  }
}

function statusBadge(status: string | null) {
  if (!status) return <Badge variant="outline" className="text-xs">—</Badge>;
  const s = status.toLowerCase();
  // CR486: new locator statuses
  if (s === "locator_ok" || s === "locator_heuristic_bbox")
    return <Badge className="bg-green-600 text-xs">{status}</Badge>;
  if (s === "locator_model_failed" || s === "locator_missing_image")
    return <Badge variant="destructive" className="text-xs">{status}</Badge>;
  if (s === "locator_bbox_full_page" || s === "locator_bbox_oversized")
    return <Badge variant="destructive" className="text-xs">{status}</Badge>;
  if (s === "locator_no_match_on_page" || s === "locator_ambiguous_multiple_panels")
    return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  switch (s) {
    case "located":
    case "cropped":
    case "accepted":
    case "uploaded":
      return <Badge className="bg-green-600 text-xs">{status}</Badge>;
    case "not_localizable":
      return <Badge variant="secondary" className="text-xs">Not Localizable</Badge>;
    case "rejected":
    case "rejected_bbox_full_page":
    case "rejected_bbox_oversized":
      return <Badge variant="destructive" className="text-xs">{status}</Badge>;
    default:
      return <Badge variant="outline" className="text-xs">{status}</Badge>;
  }
}

export default function FlyerSnippetArtifacts() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const batchRunId = searchParams.get("batch_run_id") ?? "";
  const deepLinkStagingId = searchParams.get("staging_item_id") ?? null;
  const deepLinkPage = searchParams.get("page_number") ? Number(searchParams.get("page_number")) : null;
  const [snippets, setSnippets] = useState<SnippetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [merchantMap, setMerchantMap] = useState<Record<string, string>>({});
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [deltaSort, setDeltaSort] = useState<"desc" | "asc" | null>(null);
  const [debugView, setDebugView] = useState(false); // CR579: Proposal vs Final debug toggle
  const [serverCounts, setServerCounts] = useState<{total:number;located:number;rejected:number;not_localizable:number;missing_asset:number} | null>(null);
  const inFlightRef = useRef(false);
  const [selectedSnippet, setSelectedSnippet] = useState<SnippetRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const deepLinkApplied = useRef(false);
  // CR544: Attempt tracking
  const [attempts, setAttempts] = useState<AttemptInfo[]>([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState<string | null>(null);
  // CR544 Phase 3: Previous attempt's snippets for delta comparison
  const [prevAttemptSnippets, setPrevAttemptSnippets] = useState<SnippetRow[]>([]);
  // CR544-fix: Fallback warning
  const [fallbackUsed, setFallbackUsed] = useState<string | null>(null);

  // Derived: compute delta for each row
  // CR544 Phase 3: Cross-attempt delta — compares current vs previous attempt's snippets
  type AttemptDeltaState = 'IMPROVED' | 'REGRESSED' | 'NEUTRAL' | 'NEW' | 'MISSING_PREV';
  type SnippetRowWithDelta = SnippetRow & {
    _delta: BboxDelta | null;
    _deltaClass: DeltaClass | null;
    _attemptDelta: AttemptDeltaState | null;
    _prevEvidence: number | null;
    _locatorStatus: LocatorDebugStatus;
  };

  // Build lookup map for previous attempt snippets
  const prevSnippetMap = useMemo(() => {
    const map = new Map<string, SnippetRow>();
    for (const s of prevAttemptSnippets) {
      if (s.staging_item_id && s.page_number != null) {
        map.set(`${s.staging_item_id}:${s.page_number}`, s);
      }
    }
    return map;
  }, [prevAttemptSnippets]);

  const snippetsWithDelta: SnippetRowWithDelta[] = useMemo(() =>
    snippets.map(s => {
      const d = safeBboxDelta(s.pass_a_bbox_norm, (s.pass_b as any)?.bbox_norm);
      const deltaClass = d ? classifyDelta(d, !!s.cropper_used) : null;

      // Cross-attempt delta
      let attemptDelta: AttemptDeltaState | null = null;
      let prevEvidence: number | null = null;
      if (prevAttemptSnippets.length > 0 && s.staging_item_id && s.page_number != null) {
        const prev = prevSnippetMap.get(`${s.staging_item_id}:${s.page_number}`);
        if (!prev) {
          attemptDelta = 'NEW';
        } else {
          const currEvid = (s as any).evidence_score ?? null;
          const prevEvid = (prev as any).evidence_score ?? null;
          prevEvidence = prevEvid;
          const currBbox = s.bbox_norm;
          const prevBbox = prev.bbox_norm;
          const bboxDeltaVal = safeBboxDelta(prevBbox, currBbox);

          if (currEvid != null && prevEvid != null) {
            if (currEvid > prevEvid + 0.05) attemptDelta = 'IMPROVED';
            else if (currEvid < prevEvid - 0.05) attemptDelta = 'REGRESSED';
            else attemptDelta = 'NEUTRAL';
          } else if (bboxDeltaVal) {
            const iou = bboxDeltaVal.iou;
            if (iou >= 0.8) attemptDelta = 'NEUTRAL';
            else if (s.snippet_asset_path && !prev.snippet_asset_path) attemptDelta = 'IMPROVED';
            else attemptDelta = 'NEUTRAL';
          } else {
            attemptDelta = 'NEUTRAL';
          }
        }
      }

      return { ...s, _delta: d, _deltaClass: deltaClass, _attemptDelta: attemptDelta, _prevEvidence: prevEvidence, _locatorStatus: deriveLocatorStatus(s) };
    }),
  [snippets, prevSnippetMap, prevAttemptSnippets.length]);

  const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

  const fetchSnippets = useCallback(async (attempt = 0, attemptIdOverride?: string | null) => {
    if (!batchRunId) return;
    if (attempt === 0 && inFlightRef.current) return;
    if (attempt === 0) inFlightRef.current = true;

    setLoading(true);
    if (attempt === 0) setFetchError(null);

    try {
      const body: Record<string, unknown> = { batch_run_id: batchRunId };
      // CR544: Pass attempt filter if specified
      const targetAttempt = attemptIdOverride !== undefined ? attemptIdOverride : selectedAttemptId;
      if (targetAttempt) body.step_attempt_id = targetAttempt;

      const { data, error } = await supabase.functions.invoke("admin-get-flyer-snippets", {
        body,
      });

      if (error) {
        const status = (error as any).status ?? (error as any).context?.status ?? "unknown";
        const body = (error as any).context?.body ?? "";
        const msg = (error as any).message ?? String(error);
        setFetchError(`Edge function error (status=${status}): ${msg}${body ? ` | body=${body.slice(0, 300)}` : ""}`);
        return;
      }

      if (data?.ok === false) {
        if (data.error_code === "NOT_AUTHORIZED" && attempt < 6) {
          setFetchError("Restoring session…");
          await wait(350);
          return fetchSnippets(attempt + 1);
        }
        setFetchError(`${data.error_code ?? "ERROR"}: ${data.message ?? "Unknown"}${data.trace_id ? ` (trace=${data.trace_id})` : ""}`);
        return;
      }

      setSnippets(data?.snippets ?? []);
      if (data?.counts) setServerCounts(data.counts);
      // CR544: Capture attempts list
      if (data?.attempts) setAttempts(data.attempts);
      // CR544-fix: Capture fallback warning
      setFallbackUsed(data?.fallback_used ?? null);
      setFetchError(null);

      // CR544 Phase 3: Fetch previous attempt's snippets for delta comparison
      if (data?.attempts && data.attempts.length >= 2) {
        const sortedAttempts = [...data.attempts].sort((a: AttemptInfo, b: AttemptInfo) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const currentIdx = targetAttempt
          ? sortedAttempts.findIndex((a: AttemptInfo) => a.id === targetAttempt)
          : 0;
        const prevAttempt = sortedAttempts[currentIdx + 1];
        if (prevAttempt) {
          // Fetch previous attempt snippets in background
          supabase.functions.invoke("admin-get-flyer-snippets", {
            body: { batch_run_id: batchRunId, step_attempt_id: prevAttempt.id },
          }).then(({ data: prevData }) => {
            if (prevData?.ok && prevData.snippets) {
              setPrevAttemptSnippets(prevData.snippets);
            }
          }).catch(() => { /* ignore */ });
        } else {
          setPrevAttemptSnippets([]);
        }
      } else {
        setPrevAttemptSnippets([]);
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [batchRunId, selectedAttemptId]);

  // FR2: Subscribe to auth events — immediately refetch when session arrives
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        fetchSnippets(0);
      }
    });
    return () => sub?.subscription?.unsubscribe();
  }, [fetchSnippets]);

  useEffect(() => {
    if (!batchRunId) return;
    fetchSnippets(0);

    // Also fetch merchant names for staging items
    const fetchMerchants = async () => {
      const { data } = await supabase
        .from("cr075_staging_deal_items")
        .select("id, merchant_key")
        .eq("batch_run_id", batchRunId)
        .eq("source_type", "FLYER");
      if (data) {
        const map: Record<string, string> = {};
        for (const row of data) map[row.id] = row.merchant_key;
        setMerchantMap(map);
      }
    };
    fetchMerchants();
  }, [batchRunId, fetchSnippets]);

  // CR518 FR5: Deep link — auto-open preview when staging_item_id / page_number in URL
  useEffect(() => {
    if (deepLinkApplied.current || snippets.length === 0 || !deepLinkStagingId) return;
    const match = snippets.find(s =>
      s.staging_item_id === deepLinkStagingId &&
      (deepLinkPage == null || s.page_number === deepLinkPage)
    );
    if (match) {
      deepLinkApplied.current = true;
      setSelectedSnippet(match);
      setDrawerOpen(true);
    }
  }, [snippets, deepLinkStagingId, deepLinkPage]);

  const openPreview = (s: SnippetRow) => {
    setSelectedSnippet(s);
    setDrawerOpen(true);
  };

  const summary = useMemo(() => {
    if (serverCounts) return {
      located: serverCounts.located,
      rejected: serverCounts.rejected,
      notLocalizable: serverCounts.not_localizable,
      missingAsset: serverCounts.missing_asset,
      pageFallback: 0,
      croppedSnippets: snippets.filter(hasTrueCroppedSnippet).length,
      total: serverCounts.total,
    };
    const located = snippets.filter(s =>
      ["located", "cropped", "uploaded", "accepted", "locator_ok", "locator_heuristic_bbox"].includes((s.snippet_status ?? "").toLowerCase())
    ).length;
    const rejected = snippets.filter(s =>
      (s.bbox_reject_reason && s.bbox_reject_reason !== "none") ||
      (s.snippet_status ?? "").toLowerCase().startsWith("rejected") ||
      (s.snippet_status ?? "").toLowerCase().startsWith("locator_bbox")
    ).length;
    const notLocalizable = snippets.filter(s =>
      ["not_localizable", "locator_no_match_on_page", "locator_ambiguous_multiple_panels", "locator_model_failed", "locator_missing_image"].includes((s.snippet_status ?? "").toLowerCase())
    ).length;
    const missingAsset = snippets.filter(s =>
      s.bbox_norm && !s.snippet_asset_path
    ).length;
    const pageFallback = snippets.filter(isPageFallbackRow).length;
    const croppedSnippets = snippets.filter(hasTrueCroppedSnippet).length;
    const cropFailed = snippets.filter(s => {
      const pb = s.pass_b as any;
      return pb?.attempted && !pb?.used;
    }).length;
    return { located, rejected, notLocalizable, missingAsset, pageFallback, croppedSnippets, cropFailed, total: snippets.length };
  }, [snippets, serverCounts]);

  const statusOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of snippets) {
      const st = s.snippet_status ?? "unknown";
      counts[st] = (counts[st] ?? 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [snippets]);

  // CR578: Locator status counts for summary + filter chips
  const locatorStatusCounts = useMemo(() => {
    const counts: Record<LocatorDebugStatus, number> = {
      ok: 0, ok_soft: 0, ok_soft_fallback: 0, truth_override: 0, gate_failed: 0, ocr_mismatch: 0, no_match_on_page: 0, last_resort: 0, cropper_only: 0, full_page_ok: 0, unknown: 0,
    };
    for (const s of snippetsWithDelta) {
      counts[s._locatorStatus]++;
    }
    return counts;
  }, [snippetsWithDelta]);

  const filteredSnippets = useMemo(() => {
    let result = snippetsWithDelta;
    if (statusFilter === "__low_score") {
      result = result.filter(s => s.bbox_score != null && s.bbox_score < 1);
    } else if (statusFilter === "__delta_bad") {
      result = result.filter(s => s._deltaClass === "bad");
    } else if (statusFilter === "__attempt_improved") {
      result = result.filter(s => s._attemptDelta === "IMPROVED");
    } else if (statusFilter === "__attempt_regressed") {
      result = result.filter(s => s._attemptDelta === "REGRESSED");
    } else if (statusFilter === "__copied_forward") {
      result = result.filter(s => s.copied_forward === true);
    // CR578: Locator debug status filters
    } else if (statusFilter?.startsWith("__locator_")) {
      const locSt = statusFilter.replace("__locator_", "") as LocatorDebugStatus;
      result = result.filter(s => s._locatorStatus === locSt);
    } else if (statusFilter) {
      result = result.filter(s => (s.snippet_status ?? "unknown") === statusFilter);
    }
    if (deltaSort) {
      result = [...result].sort((a, b) => {
        const ai = a._delta?.iou ?? -1;
        const bi = b._delta?.iou ?? -1;
        return deltaSort === "desc" ? bi - ai : ai - bi;
      });
    }
    return result;
  }, [snippetsWithDelta, statusFilter, deltaSort]);

  const handleCopyBatchId = () => {
    navigator.clipboard.writeText(batchRunId);
    toast({ title: "Copied", description: "Batch run ID copied to clipboard" });
  };

  if (!batchRunId) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No batch_run_id provided.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <FileImage className="h-5 w-5" />
            Snippet Artifacts
          </h1>
          <button
            onClick={handleCopyBatchId}
            className="text-xs text-muted-foreground font-mono flex items-center gap-1 hover:text-foreground transition-colors"
            title="Click to copy full batch ID"
          >
            batch: {batchRunId.slice(0, 8)}…
            <Copy className="h-3 w-3" />
          </button>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          disabled={loading}
          onClick={() => fetchSnippets(0)}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {/* CR579: Debug view toggle */}
        <div className="flex items-center gap-1.5 ml-2">
          <Switch
            id="debug-view"
            checked={debugView}
            onCheckedChange={setDebugView}
            className="h-4 w-7"
          />
          <label htmlFor="debug-view" className="text-[10px] text-muted-foreground cursor-pointer select-none">
            Debug: Prop vs Final
          </label>
        </div>
      </div>

      {/* CR544A: Diamond Mode status strip */}
      {attempts.length > 0 && (() => {
        const currentAttempt = selectedAttemptId 
          ? attempts.find(a => a.id === selectedAttemptId) 
          : attempts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        const isDiamond = currentAttempt?.effective_run_policy === 'diamond';
        return (
          <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-md border ${isDiamond ? 'bg-primary/5 border-primary/20' : 'bg-muted/40 border-muted'}`}>
            {isDiamond ? <Gem className="h-3.5 w-3.5 text-primary" /> : <Square className="h-3.5 w-3.5 text-muted-foreground" />}
            <span className="font-medium">{isDiamond ? 'Diamond Mode' : 'Stable Mode'}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground font-mono">
              {currentAttempt?.fn_version ? `v${currentAttempt.fn_version}` : 'version unknown'}
            </span>
            {currentAttempt?.skipped_reason && (
              <>
                <span className="text-muted-foreground">·</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{currentAttempt.skipped_reason}</Badge>
              </>
            )}
            {/* CR546: Attempt-scoped indicator */}
            {isDiamond && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className={`font-medium ${currentAttempt?.artifact_write_scope === 'attempt' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                  Attempt-scoped: {currentAttempt?.artifact_write_scope === 'attempt' ? '✅' : '❌'}
                </span>
              </>
            )}
          </div>
        );
      })()}

      {/* CR561: Zero-artifact red banner — no silent fallback */}
      {!loading && !fallbackUsed && snippets.length === 0 && attempts.length > 0 && (
        <div className="flex items-center gap-2 text-sm px-4 py-3 rounded-md border-2 border-destructive/40 bg-destructive/5">
          <AlertCircle className="h-5 w-5 text-destructive" />
          <span className="font-semibold text-destructive">
            0 artifacts written for this attempt — check S1.4 wiring or re-run.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto text-xs"
            onClick={() => {
              // Opt-in to legacy fallback
              setFallbackUsed("user_requested");
              // Re-fetch with allow_fallback=true
              const body: Record<string, unknown> = { batch_run_id: batchRunId, allow_fallback: true };
              supabase.functions.invoke("admin-get-flyer-snippets", { body }).then(({ data }) => {
                if (data?.ok && data.snippets) {
                  setSnippets(data.snippets);
                  if (data.counts) setServerCounts(data.counts);
                  setFallbackUsed(data.fallback_used ?? "user_requested");
                }
              });
            }}
          >
            Show legacy artifacts
          </Button>
        </div>
      )}

      {/* CR544-fix: Fallback warning when using legacy rows */}
      {fallbackUsed && (
        <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-amber-300/40 bg-amber-50/50 dark:bg-amber-900/10">
          <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-amber-700 dark:text-amber-400 font-medium">
            Showing {fallbackUsed === 'legacy_null_attempt' ? 'legacy (pre-Diamond) artifacts' : fallbackUsed === 'user_requested' ? 'legacy artifacts (manually requested)' : 'most recent available snippets'} — not from latest attempt.
          </span>
        </div>
      )}

      {/* CR545-final: Diamond Mode active but 0 attempt-scoped artifacts warning */}
      {(() => {
        const currentAttempt = selectedAttemptId 
          ? attempts.find(a => a.id === selectedAttemptId) 
          : attempts.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        const isDiamond = currentAttempt?.effective_run_policy === 'diamond';
        const hasAttemptArtifacts = snippets.some(s => s.step_attempt_id != null);
        // CR557: "Cracked Diamond" state — writes going to legacy despite Diamond policy
        const hasCrackedDiamond = isDiamond && currentAttempt && 
          (currentAttempt as any).attempt_scoped_written_count === 0 && 
          (currentAttempt as any).legacy_written_count_during_attempt > 0;
        
        if (hasCrackedDiamond) {
          return (
            <div className="flex items-center gap-2 text-sm px-4 py-3 rounded-md border-2 border-destructive/40 bg-destructive/5">
              <Gem className="h-5 w-5 text-destructive" />
              <span className="font-semibold text-destructive">
                💎🔻 Cracked Diamond: Writes are going to legacy — step_attempt_id missing. Check attempt creation.
              </span>
            </div>
          );
        }
        if (isDiamond && snippets.length === 0 && !loading && !fallbackUsed) {
          return (
            <div className="flex items-center gap-2 text-sm px-4 py-3 rounded-md border-2 border-destructive/40 bg-destructive/5">
              <Gem className="h-5 w-5 text-destructive" />
              <span className="font-semibold text-destructive">
                💎 Diamond Mode active, but 0 attempt-scoped artifacts were written — check step_attempt_id wiring in S1.4.
              </span>
            </div>
          );
        }
        if (isDiamond && snippets.length > 0 && !hasAttemptArtifacts && !fallbackUsed) {
          return (
            <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-amber-400/40 bg-amber-50/50 dark:bg-amber-900/10">
              <Gem className="h-4 w-4 text-amber-600" />
              <span className="text-amber-700 dark:text-amber-400 font-medium">
                💎 Diamond Mode active, but all {snippets.length} artifacts have NULL step_attempt_id — these are legacy rows, not attempt-scoped.
              </span>
            </div>
          );
        }
        return null;
      })()}

      {/* CR544-fix: Copied-forward count */}
      {snippets.length > 0 && snippets.some(s => s.copied_forward) && (
        <div className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-primary/20 bg-primary/5">
          <ClipboardCopy className="h-3.5 w-3.5 text-primary" />
          <span className="font-medium">
            {snippets.filter(s => s.copied_forward).length} of {snippets.length} snippets copied forward from prior attempt
          </span>
        </div>
      )}

      {/* CR544: Attempt selector */}
      {attempts.length > 1 && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground font-medium">Attempt:</span>
          <Select
            value={selectedAttemptId ?? "latest"}
            onValueChange={(val) => {
              const newId = val === "latest" ? null : val;
              setSelectedAttemptId(newId);
              fetchSnippets(0, newId);
            }}
          >
            <SelectTrigger className="w-[420px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest" className="text-xs">Latest (default)</SelectItem>
              {[...attempts].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((a) => (
                <SelectItem key={a.id} value={a.id} className="text-xs">
                  <span className="inline-flex items-center gap-1">
                    {a.effective_run_policy === 'diamond' ? <Gem className="h-3 w-3" /> : <Square className="h-3 w-3" />}
                    Attempt #{a.attempt_no} — {new Date(a.created_at).toLocaleString()}
                    {a.status !== 'COMPLETE' && ` — ${a.status}`}
                    {a.skipped_reason && ` — ${a.skipped_reason}`}
                    {a.records_out != null && ` — ${a.records_out} out`}
                    {a.fn_version && ` — v${a.fn_version.slice(0, 20)}`}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-[10px]">
            {attempts.length} attempts
          </Badge>
        </div>
      )}

      {/* Status filter */}
      <div className="flex flex-wrap gap-1.5">
        <Badge
          variant={statusFilter === null ? "default" : "outline"}
          className="text-xs cursor-pointer"
          onClick={() => setStatusFilter(null)}
        >All ({snippets.length})</Badge>
        <Badge
          variant={statusFilter === "__low_score" ? "default" : "outline"}
          className="text-xs cursor-pointer"
          onClick={() => setStatusFilter("__low_score")}
        >Low score (&lt;1)</Badge>
        <Badge
          variant={statusFilter === "__delta_bad" ? "default" : "outline"}
          className="text-xs cursor-pointer"
          onClick={() => setStatusFilter("__delta_bad")}
        >Δ: Bad</Badge>
        {prevAttemptSnippets.length > 0 && (
          <>
            <Badge
              variant={statusFilter === "__attempt_improved" ? "default" : "outline"}
              className="text-xs cursor-pointer"
              onClick={() => setStatusFilter("__attempt_improved")}
            >✅ Improved</Badge>
            <Badge
              variant={statusFilter === "__attempt_regressed" ? "default" : "outline"}
              className="text-xs cursor-pointer"
              onClick={() => setStatusFilter("__attempt_regressed")}
            >⚠️ Regressed</Badge>
            <Badge
              variant={statusFilter === "__copied_forward" ? "default" : "outline"}
              className="text-xs cursor-pointer"
              onClick={() => setStatusFilter("__copied_forward")}
            >📋 Copied Forward</Badge>
          </>
        )}
        {statusOptions.map(([st, count]) => (
          <Badge
            key={st}
            variant={statusFilter === st ? "default" : "outline"}
            className="text-xs cursor-pointer"
            onClick={() => setStatusFilter(st)}
          >{st} ({count})</Badge>
        ))}
      </div>

      {/* CR578: Locator debug filter chips */}
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-[10px] text-muted-foreground font-medium mr-1">Locator:</span>
        {(Object.keys(LOCATOR_STATUS_CONFIG) as LocatorDebugStatus[])
          .filter(k => locatorStatusCounts[k] > 0)
          .map(k => (
            <Badge
              key={k}
              variant={statusFilter === `__locator_${k}` ? "default" : "outline"}
              className="text-[10px] cursor-pointer"
              onClick={() => setStatusFilter(statusFilter === `__locator_${k}` ? null : `__locator_${k}`)}
            >
              {LOCATOR_STATUS_CONFIG[k].label} ({locatorStatusCounts[k]})
            </Badge>
          ))
        }
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-8 gap-2">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-lg font-semibold">{summary.total}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Located</div>
          <div className="text-lg font-semibold text-green-600">{summary.located}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">✅ Snippet Cropped</div>
          <div className="text-lg font-semibold text-green-600">
            {summary.croppedSnippets}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">📄 Page Fallback</div>
          <div className="text-lg font-semibold text-amber-600">
            {summary.pageFallback}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">⚠️ Crop Needed</div>
          <div className="text-lg font-semibold text-amber-600">{summary.missingAsset}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">❌ Crop Failed</div>
          <div className="text-lg font-semibold text-destructive">{summary.cropFailed}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Rejected (bbox)</div>
          <div className="text-lg font-semibold text-destructive">{summary.rejected}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs text-muted-foreground">Not Localizable</div>
          <div className="text-lg font-semibold">{summary.notLocalizable}</div>
        </Card>
      </div>

      {/* CR578: Locator summary line */}
      {snippets.length > 0 && (
        <div className="text-[11px] text-muted-foreground px-1">
          Locator summary:{' '}
          {(Object.keys(LOCATOR_STATUS_CONFIG) as LocatorDebugStatus[])
            .filter(k => locatorStatusCounts[k] > 0)
            .map(k => `${locatorStatusCounts[k]} ${LOCATOR_STATUS_CONFIG[k].label}`)
            .join(' · ')}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {fetchError === "Restoring session…" && (
                <p className="text-sm text-muted-foreground mb-2">Restoring session…</p>
              )}
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : fetchError && fetchError !== "Restoring session…" ? (
            <div className="p-6 border border-destructive/30 bg-destructive/5 rounded-md m-4">
              <div className="flex items-center gap-2 text-destructive font-medium mb-1">
                <AlertCircle className="h-4 w-4" />
                Failed to load snippet artifacts
              </div>
              <p className="text-sm text-muted-foreground">{fetchError}</p>
            </div>
          ) : fetchError === "Restoring session…" ? (
            <div className="p-6 border border-amber-300/30 bg-amber-50/50 dark:bg-amber-900/10 rounded-md m-4">
              <p className="text-sm text-muted-foreground">Restoring session… please wait.</p>
            </div>
          ) : snippets.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No snippet artifacts found for this batch run.</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                   <TableRow>
                     <TableHead className="w-[40px]"></TableHead>
                     <TableHead className="w-[120px]">Merchant</TableHead>
                     <TableHead className="w-[60px] text-center">Page</TableHead>
                     <TableHead className="w-[90px]">Status</TableHead>
                     <TableHead className="w-[80px]">Locator</TableHead>
                     <TableHead className="w-[80px]">Quality</TableHead>
                     <TableHead className="w-[60px] text-center">Gate</TableHead>
                     {debugView && <TableHead className="w-[200px]">Prop vs Final</TableHead>}
                     <TableHead className="w-[80px] text-center">Area Ratio</TableHead>
                     <TableHead className="w-[60px] text-center">Score</TableHead>
                     <TableHead className="w-[50px] text-center">Hits</TableHead>
                     <TableHead className="w-[70px] text-center">Area Pen.</TableHead>
                     <TableHead className="w-[70px] text-center">Ovlp Pen.</TableHead>
                     <TableHead className="w-[120px]">Reject Reason</TableHead>
                     <TableHead className="w-[80px]">Asset</TableHead>
                     <TableHead className="w-[100px]">Render Source</TableHead>
                     <TableHead
                       className="w-[90px] text-center cursor-pointer select-none"
                       onClick={() => setDeltaSort(prev => prev === "desc" ? "asc" : prev === "asc" ? null : "desc")}
                     >
                       <span className="inline-flex items-center gap-0.5">
                         Δ
                         {deltaSort === "desc" && <ArrowDown className="h-3 w-3" />}
                         {deltaSort === "asc" && <ArrowUp className="h-3 w-3" />}
                       </span>
                      </TableHead>
                      {prevAttemptSnippets.length > 0 && (
                        <TableHead className="w-[90px] text-center">Attempt Δ</TableHead>
                      )}
                    </TableRow>
                </TableHeader>
                <TableBody>
                   {filteredSnippets.map((s, i) => (
                     <TableRow
                       key={i}
                       className="cursor-pointer"
                       onClick={() => openPreview(s)}
                     >
                       <TableCell className="p-1 text-center">
                         <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openPreview(s); }}>
                           <Eye className="h-3.5 w-3.5" />
                         </Button>
                       </TableCell>
                       <TableCell className="font-medium text-sm truncate max-w-[120px]">
                         {(s.staging_item_id && merchantMap[s.staging_item_id]) || s.deal_id?.slice(0, 8) || "—"}
                       </TableCell>
                       <TableCell className="text-center text-sm">{s.page_number ?? "—"}</TableCell>
                       <TableCell>
                         <div className="flex items-center gap-1">
                           {statusBadge(s.snippet_status)}
                           {/* CR544A: Row-level copied-forward icon */}
                           {s.copied_forward && (
                             <TooltipProvider>
                               <Tooltip>
                                 <TooltipTrigger asChild>
                                   <ClipboardCopy className="h-3 w-3 text-muted-foreground shrink-0" />
                                 </TooltipTrigger>
                                 <TooltipContent className="text-xs">
                                   Copied from attempt {s.copied_from_attempt_id?.slice(0, 8)}…
                                   {s.copy_reason && <><br />{s.copy_reason}</>}
                                 </TooltipContent>
                               </Tooltip>
                             </TooltipProvider>
                           )}
                           {/* CR544A: Recompute hint icon */}
                           {!s.copied_forward && s.cropper_used && (
                             <TooltipProvider>
                               <Tooltip>
                                 <TooltipTrigger asChild>
                                   <Wrench className="h-3 w-3 text-muted-foreground shrink-0" />
                                 </TooltipTrigger>
                                 <TooltipContent className="text-xs">Recomputed (Pass B ran)</TooltipContent>
                               </Tooltip>
                             </TooltipProvider>
                           )}
                         </div>
                        </TableCell>
                       {/* CR578: Locator debug status column */}
                       <TableCell>
                         {locatorStatusBadge(s._locatorStatus, s)}
                       </TableCell>
                       <TableCell>
                         {/* CR575: Show quality only when final bbox exists */}
                         {s.final_bbox_source ? qualityBadge(s.bbox_quality_hint) : <span className="text-xs text-muted-foreground">—</span>}
                       </TableCell>
                       <TableCell className="text-center">
                         {/* CR575: Anchor gate pill */}
                         {s.anchor_gate_result ? (
                           <TooltipProvider>
                             <Tooltip>
                               <TooltipTrigger asChild>
                                 <Badge
                                   variant={s.anchor_gate_result === 'passed' ? 'default' : s.anchor_gate_result === 'rejected' ? 'destructive' : 'secondary'}
                                   className="text-[10px] cursor-help"
                                 >
                                   {s.anchor_gate_result === 'passed' ? '✓' : s.anchor_gate_result === 'rejected' ? '✗' : '—'}
                                 </Badge>
                               </TooltipTrigger>
                               <TooltipContent side="left" className="text-xs max-w-xs space-y-0.5">
                                 <p><strong>Gate:</strong> {s.anchor_gate_result}</p>
                                 {s.anchor_gate_reject_reason && <p><strong>Reason:</strong> {s.anchor_gate_reject_reason}</p>}
                                  {s.anchor_gate_must_hit_count != null && (
                                    <p><strong>Must-hits:</strong> {s.anchor_gate_must_hit_count}/{s.anchor_gate_must_hit_required ?? '?'}</p>
                                  )}
                                  {s.locator_proposed_confidence != null && (
                                    <p><strong>Proposed conf:</strong> {(s.locator_proposed_confidence * 100).toFixed(0)}%</p>
                                  )}
                                  {(s as any).box_ocr_engine && (
                                    <p><strong>OCR:</strong> {(s as any).box_ocr_engine}{(s as any).box_ocr_status === 'error' ? ' ⚠' : ''}</p>
                                  )}
                                   {(s as any).anchor_gate_rule_version && (
                                     <p><strong>Rule:</strong> {(s as any).anchor_gate_rule_version}</p>
                                   )}
                                     {(s as any).ocr_sanity_status && (
                                       <p><strong>OCR sanity:</strong> {(s as any).ocr_sanity_status === 'flaky' ? '⚠ flaky' : (s as any).ocr_sanity_status}</p>
                                     )}
                                     {(s as any).ocr_page_source && (
                                       <p><strong>OCR src:</strong> {
                                         (s as any).ocr_page_source?.startsWith('truth:')
                                           ? `🟢 ${(s as any).ocr_page_source}`
                                           : (s as any).ocr_page_source === 's1_4_fresh'
                                             ? '🔵 fresh (S1.4)'
                                             : (s as any).ocr_page_source === 'deal.ocr_text'
                                               ? '📦 deal.ocr_text (legacy)'
                                               : `📦 ${(s as any).ocr_page_source}`
                                       }</p>
                                     )}
                                </TooltipContent>
                             </Tooltip>
                           </TooltipProvider>
                         ) : <span className="text-xs text-muted-foreground">—</span>}
                       </TableCell>
                       {/* CR579: Prop vs Final debug column */}
                       {debugView && (
                         <TableCell className="text-[10px] font-mono leading-tight">
                           {(() => {
                             const prop = (s as any).locator_proposed_bbox_norm as any;
                             const fin = s.bbox_norm as any;
                             const fmtBbox = (b: any) => b ? `${(b.width ?? 0).toFixed(2)}×${(b.height ?? 0).toFixed(2)} @(${(b.x ?? 0).toFixed(2)},${(b.y ?? 0).toFixed(2)})` : '—';
                             const isMatch = prop && fin && 
                               Math.abs(prop.x - fin.x) < 0.01 && Math.abs(prop.y - fin.y) < 0.01 &&
                               Math.abs(prop.width - fin.width) < 0.01 && Math.abs(prop.height - fin.height) < 0.01;
                             if (!prop) return <span className="text-muted-foreground">no proposal</span>;
                             return (
                               <div className="space-y-0.5">
                                 <div>P: {fmtBbox(prop)}</div>
                                 <div>F: {fin ? fmtBbox(fin) : <span className="text-destructive">none</span>}{isMatch ? ' ✅' : ''}</div>
                               </div>
                             );
                           })()}
                         </TableCell>
                       )}
                       <TableCell className="text-center text-sm font-mono">
                         {/* CR575: Show area only when final bbox exists */}
                         {s.final_bbox_source && s.bbox_area_ratio != null ? s.bbox_area_ratio.toFixed(3) : "—"}
                       </TableCell>
                       <TableCell className="text-center text-sm font-mono">
                         {s.bbox_score != null ? s.bbox_score.toFixed(2) : "—"}
                       </TableCell>
                       <TableCell className="text-center text-sm font-mono">
                         {s.bbox_score_components?.key_phrase_hits ?? "—"}
                       </TableCell>
                       <TableCell className="text-center text-sm font-mono">
                         {s.bbox_score_components?.area_penalty != null ? s.bbox_score_components.area_penalty.toFixed(2) : "—"}
                       </TableCell>
                       <TableCell className="text-center text-sm font-mono">
                         {s.bbox_score_components?.overlap_penalty != null ? s.bbox_score_components.overlap_penalty.toFixed(2) : "—"}
                       </TableCell>
                       <TableCell className="text-xs text-muted-foreground truncate max-w-[120px]">
                         {s.bbox_reject_reason && s.bbox_reject_reason !== "none" ? s.bbox_reject_reason : "—"}
                       </TableCell>
                        <TableCell>
                          {(() => {
                            const pb = s.pass_b as any;
                            if (isPageFallbackRow(s)) {
                              return <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">📄 Page fallback</Badge>;
                            }
                            if (hasTrueCroppedSnippet(s)) {
                              return <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/20">✅ Cropped</Badge>;
                            }
                            if (pb?.attempted && !pb?.used) {
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-xs bg-red-50 dark:bg-red-900/20 text-destructive cursor-help">❌ Crop failed</Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" className="text-xs max-w-xs space-y-1">
                                      <p><strong>Reason:</strong> {pb.reason || "unknown"}</p>
                                      {pb.error && <p><strong>Error:</strong> {pb.error}</p>}
                                      {pb.parse_mode && <p><strong>Parse mode:</strong> {pb.parse_mode}</p>}
                                      {pb.bbox_raw && <p><strong>Raw bbox:</strong> {JSON.stringify(pb.bbox_raw)}</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }
                            if (s.bbox_norm) {
                              return <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">⚠️ Crop needed</Badge>;
                            }
                            if (s.snippet_status?.toLowerCase().includes("failed") || s.snippet_status?.toLowerCase().includes("rejected")) {
                              return <Badge variant="outline" className="text-xs bg-red-50 dark:bg-red-900/20 text-destructive">❌ Crop failed</Badge>;
                            }
                            return <Badge variant="outline" className="text-xs">missing</Badge>;
                          })()}
                        </TableCell>
                       <TableCell className="text-xs font-mono text-muted-foreground">
                         {s.render_source || "—"}
                       </TableCell>
                       <TableCell className="text-center">
                         {s._delta ? (
                           <TooltipProvider>
                             <Tooltip>
                               <TooltipTrigger asChild>
                                 <Badge
                                   variant={s._deltaClass === "good" ? "default" : s._deltaClass === "bad" ? "destructive" : "secondary"}
                                   className="text-[10px] cursor-help"
                                 >
                                   Δ {s._deltaClass === "good" ? "Good" : s._deltaClass === "bad" ? "Bad" : "Ok"} · IoU {s._delta.iou.toFixed(2)}
                                 </Badge>
                               </TooltipTrigger>
                               <TooltipContent side="left" className="text-xs space-y-0.5 max-w-xs">
                                 <p>IoU: {s._delta.iou.toFixed(3)}</p>
                                 <p>Area×: {s._delta.areaMult.toFixed(3)}</p>
                                 <p>Shift: {s._delta.shift.toFixed(3)}</p>
                                 <p>Final: {s.final_bbox_source ?? "unknown"}</p>
                                 <p>Cropper: {s.cropper_used ? "yes" : "no"}</p>
                               </TooltipContent>
                             </Tooltip>
                           </TooltipProvider>
                         ) : (
                           <span className="text-xs text-muted-foreground">—</span>
                         )}
                        </TableCell>
                        {prevAttemptSnippets.length > 0 && (
                          <TableCell className="text-center">
                            {s._attemptDelta ? (
                              <Badge
                                variant={
                                  s._attemptDelta === 'IMPROVED' ? 'default' :
                                  s._attemptDelta === 'REGRESSED' ? 'destructive' :
                                  s._attemptDelta === 'NEW' ? 'outline' : 'secondary'
                                }
                                className="text-[10px]"
                              >
                                {s._attemptDelta === 'IMPROVED' && '✅ Improved'}
                                {s._attemptDelta === 'REGRESSED' && '⚠️ Regressed'}
                                {s._attemptDelta === 'NEUTRAL' && '➖ No change'}
                                {s._attemptDelta === 'NEW' && '🆕 New'}
                                {s._attemptDelta === 'MISSING_PREV' && '—'}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                   ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* CR518: Snippet Preview Drawer */}
      <SnippetPreviewDrawer
        snippet={selectedSnippet ? {
          ...selectedSnippet,
          bbox_norm: selectedSnippet.bbox_norm as any,
          pass_a_bbox_norm: selectedSnippet.pass_a_bbox_norm as any,
          pass_b: selectedSnippet.pass_b as any,
          cropper_used: selectedSnippet.cropper_used,
          final_bbox_source: selectedSnippet.final_bbox_source as any,
          merchantName: selectedSnippet.staging_item_id ? merchantMap[selectedSnippet.staging_item_id] : undefined,
          bbox_hint_present: (selectedSnippet as any).bbox_hint_present ?? null,
          // CR574: Image grounding + box OCR diagnostics
          has_image_sent: (selectedSnippet as any).has_image_sent ?? null,
          ai_payload_has_image: (selectedSnippet as any).ai_payload_has_image ?? (selectedSnippet as any).has_image_sent ?? null,
          box_ocr_text_200: (selectedSnippet as any).box_ocr_text_200 ?? null,
          box_must_hit_count: (selectedSnippet as any).box_must_hit_count ?? null,
          box_should_hit_count: (selectedSnippet as any).box_should_hit_count ?? null,
          box_anchor_hits: (selectedSnippet as any).box_anchor_hits ?? null,
          fallback_policy: (selectedSnippet as any).fallback_policy ?? null,
          anchor_gate_result: (selectedSnippet as any).anchor_gate_result ?? null,
          anchor_gate_reject_reason: (selectedSnippet as any).anchor_gate_reject_reason ?? null,
          must_hit_tokens_used: (selectedSnippet as any).must_hit_tokens_used ?? null,
          should_hit_tokens_used: (selectedSnippet as any).should_hit_tokens_used ?? null,
          // CR575: Locator proposed metrics
          locator_proposed_bbox_norm: (selectedSnippet as any).locator_proposed_bbox_norm ?? null,
          locator_proposed_area_ratio: (selectedSnippet as any).locator_proposed_area_ratio ?? null,
          locator_proposed_confidence: (selectedSnippet as any).locator_proposed_confidence ?? null,
          locator_proposed_quality_hint: (selectedSnippet as any).locator_proposed_quality_hint ?? null,
          anchor_gate_must_hit_count: (selectedSnippet as any).anchor_gate_must_hit_count ?? null,
          anchor_gate_should_hit_count: (selectedSnippet as any).anchor_gate_should_hit_count ?? null,
          anchor_gate_must_hit_required: (selectedSnippet as any).anchor_gate_must_hit_required ?? null,
          anchor_gate_anchor_hits: (selectedSnippet as any).anchor_gate_anchor_hits ?? null,
          // CR577: Proposal box OCR diagnostics
          proposal_box_must_hit_count: (selectedSnippet as any).proposal_box_must_hit_count ?? null,
          proposal_box_must_hit_total: (selectedSnippet as any).proposal_box_must_hit_total ?? null,
          proposal_box_should_hit_count: (selectedSnippet as any).proposal_box_should_hit_count ?? null,
          proposal_box_anchor_hits: (selectedSnippet as any).proposal_box_anchor_hits ?? null,
          proposal_box_missing_must: (selectedSnippet as any).proposal_box_missing_must ?? null,
          proposal_box_missing_should: (selectedSnippet as any).proposal_box_missing_should ?? null,
          proposal_box_ocr_text_200: (selectedSnippet as any).proposal_box_ocr_text_200 ?? null,
          anchor_gate_threshold_min_must: (selectedSnippet as any).anchor_gate_threshold_min_must ?? null,
          anchor_gate_threshold_min_total: (selectedSnippet as any).anchor_gate_threshold_min_total ?? null,
          // CR578: Core/support diagnostics
          proposal_core_hits: (selectedSnippet as any).proposal_core_hits ?? null,
          proposal_support_hits: (selectedSnippet as any).proposal_support_hits ?? null,
          proposal_core_total: (selectedSnippet as any).proposal_core_total ?? null,
          anchor_gate_rule_version: (selectedSnippet as any).anchor_gate_rule_version ?? null,
          // CR580: Per-box OCR diagnostics
          box_ocr_engine: (selectedSnippet as any).box_ocr_engine ?? null,
          box_ocr_status: (selectedSnippet as any).box_ocr_status ?? null,
          box_ocr_error: (selectedSnippet as any).box_ocr_error ?? null,
          box_ocr_duration_ms: (selectedSnippet as any).box_ocr_duration_ms ?? null,
          // CR580: OCR mismatch
          ocr_page_mismatch_detected: (selectedSnippet as any).ocr_page_mismatch_detected ?? null,
          ocr_page_merchant_hint: (selectedSnippet as any).ocr_page_merchant_hint ?? null,
          // CR581: OCR sanity
          ocr_sanity_status: (selectedSnippet as any).ocr_sanity_status ?? null,
          ocr_sanity_reason: (selectedSnippet as any).ocr_sanity_reason ?? null,
          // CR582: Page OCR source
          ocr_page_source: (selectedSnippet as any).ocr_page_source ?? null,
          ocr_page_text_len: (selectedSnippet as any).ocr_page_text_len ?? null,
          ocr_page_excerpt_200: (selectedSnippet as any).ocr_page_excerpt_200 ?? null,
          // CR585: Truth override + batch_run_id for lock button
          truth_override_source: (selectedSnippet as any).truth_override_source ?? null,
          batch_run_id: (selectedSnippet as any).batch_run_id ?? batchRunId ?? null,
          // CR587: Anchor suggestions
          anchor_suggestions: (selectedSnippet as any).anchor_suggestions ?? null,
        } : null}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedSnippet(null); }}
        debugView={debugView}
      />
    </div>
  );
}
